import { useLayoutEffect, useRef, useState } from 'react';
import { useWebformLeadsQuery } from '../api/queries';
import { toCsv, downloadCsv } from '../lib/csv';
import type { WebformLead } from '../types';

type Props = { af: string | null };

/** Stable, Excel-friendly timestamp (UTC-stored values shown as local). */
function csvDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function todayStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function exportLeadsCsv(af: string, leads: WebformLead[]): void {
  const headers = ['Date', 'First Name', 'Last Name', 'Phone', 'Email', 'Message', 'Form'];
  const rows = leads.map((l) => [
    csvDate(l.submitted_at),
    l.first_name ?? '',
    l.last_name ?? '',
    l.phone ?? '',
    l.email ?? '',
    l.message ?? '',
    l.form_title ?? '',
  ]);
  downloadCsv(`webform-leads_${af}_${todayStamp()}.csv`, toCsv(headers, rows));
}

/**
 * Message cell that clamps to 2 lines and reveals a "Show more / Show less"
 * toggle only when the text actually overflows (measured after layout).
 */
function MessageCell({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  return (
    <div className="webform-leads-msgcell">
      <div
        ref={ref}
        className={`webform-leads-message${expanded ? ' is-expanded' : ''}`}
        onClick={truncated ? () => setExpanded((v) => !v) : undefined}
      >
        {text}
      </div>
      {truncated && (
        <button
          type="button"
          className="webform-leads-more"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show less ▴' : 'Show more ▾'}
        </button>
      )}
    </div>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function fullName(l: WebformLead): string {
  const n = [l.first_name, l.last_name].filter(Boolean).join(' ').trim();
  return n || '—';
}

export default function WebformLeadsSubTab({ af }: Props) {
  const q = useWebformLeadsQuery(af);

  if (!af) {
    return (
      <div className="tab-placeholder">
        <h3>Webform Leads</h3>
        <p>Select a franchisee to view their website form submissions.</p>
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading webform leads…
      </div>
    );
  }

  if (q.error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Error loading webform leads: {(q.error as Error).message}
      </div>
    );
  }

  const leads = q.data?.leads ?? [];
  const synced = q.data?.synced_at ?? null;

  if (leads.length === 0) {
    return (
      <div className="tab-placeholder">
        <h3>Webform Leads</h3>
        <p>No website form submissions found for this franchisee yet.</p>
      </div>
    );
  }

  return (
    <>
      <div className="webform-leads-header">
        <span className="webform-leads-count">{leads.length} lead{leads.length === 1 ? '' : 's'}</span>
        {synced && <span className="webform-leads-synced">Updated {fmtDate(synced)}</span>}
        <button
          type="button"
          className="export-csv-btn"
          onClick={() => exportLeadsCsv(af, leads)}
        >
          ⬇ Download CSV
        </button>
      </div>

      <div className="calls-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Message</th>
              <th>Form</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.submitted_at)}</td>
                <td>{fullName(l)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '—'}
                </td>
                <td>{l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : '—'}</td>
                <td>
                  {l.message ? <MessageCell text={l.message} /> : '—'}
                </td>
                <td>{l.form_title || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
